import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from './services/api';
import './FLHAHistory.css';

const FLHA_SUBMISSIONS_KEY = 'fireTrainingFLHA:submissions';

const safeJsonParse = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const FLHAHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flashMessage, setFlashMessage] = useState(location.state?.successMessage || null);

  useEffect(() => {
    if (!location.state?.successMessage) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setFlashMessage(null);
      navigate(location.pathname, { replace: true, state: null });
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let mounted = true;

    const normalizeApiRow = (row) => {
      if (!row) return null;
      return {
        id: row.id,
        submittedAt: row.submittedAt,
        instructorName: row.instructorEmail || '',
        instructorDisplay: row.instructorEmail || '',
        trainingLocation: row.trainingLocation || '',
        pdfBase64: null,
        hasPdf: !!row.hasPdf
      };
    };

    const mergeById = (primary, secondary) => {
      const map = new Map();
      (Array.isArray(primary) ? primary : []).forEach(it => {
        if (!it || !it.id) return;
        map.set(String(it.id), it);
      });
      (Array.isArray(secondary) ? secondary : []).forEach(it => {
        if (!it || !it.id) return;
        const key = String(it.id);
        if (!map.has(key)) {
          map.set(key, it);
        } else {
          // Merge, preserving any stored PDF base64 when present
          const existing = map.get(key);
          map.set(key, {
            ...it,
            ...existing,
            pdfBase64: existing?.pdfBase64 || it?.pdfBase64 || null,
            hasPdf: (existing?.hasPdf ?? false) || (it?.hasPdf ?? false)
          });
        }
      });
      return Array.from(map.values());
    };

    const load = async () => {
      setLoading(true);
      setError(null);

      // Local submissions (legacy/offline fallback)
      const raw = localStorage.getItem(FLHA_SUBMISSIONS_KEY);
      const parsedLocal = safeJsonParse(raw, []);
      const localItems = Array.isArray(parsedLocal) ? parsedLocal : [];

      // Database submissions (authoritative)
      let apiItems = [];
      try {
        const resp = await API.get('/api/flha/submissions');
        const rows = resp?.data?.submissions || [];
        apiItems = (Array.isArray(rows) ? rows : []).map(normalizeApiRow).filter(Boolean);
      } catch (e) {
        console.error('Failed to load FLHA submissions from API:', e);
        setError('Failed to load FLHA history from the database. Showing locally saved items only.');
      }

      const merged = mergeById(apiItems, localItems);
      if (mounted) setItems(merged);
      if (mounted) setLoading(false);
    };

    load();
    return () => { mounted = false; };
  }, []);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => String(b.submittedAt || '').localeCompare(String(a.submittedAt || '')));
  }, [items]);

  const handleViewPDF = async (entry) => {
    try {
      if (!entry || !entry.id) {
        alert('Invalid history entry.');
        return;
      }

      const id = encodeURIComponent(String(entry.id));

      // Preferred: fetch PDF bytes directly (avoids huge base64 JSON + atob limits)
      try {
        const pdfResp = await API.get(`/api/flha/submissions/${id}/pdf`, { responseType: 'arraybuffer' });
        const url = window.URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      } catch (binaryErr) {
        console.warn('Binary PDF fetch failed; falling back to base64 JSON:', binaryErr);
      }

      // Fallback: fetch base64 from the DB and decode locally
      let pdfBase64 = entry.pdfBase64;
      if (!pdfBase64) {
        if (entry.hasPdf === false) {
          alert('PDF not available for this entry.');
          return;
        }
        const resp = await API.get(`/api/flha/submissions/${id}`);
        pdfBase64 = resp?.data?.submission?.pdfBase64 || null;
      }
      if (!pdfBase64) {
        alert('PDF not found for this entry.');
        return;
      }
      const bytes = base64ToUint8Array(pdfBase64);
      const url = window.URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Failed to open PDF:', e);
      alert('Failed to open PDF. Please check console for details.');
    }
  };

  return (
    <div>
      <h2>FLHA History</h2>

      {flashMessage && <p style={{ color: '#166534' }}>{flashMessage}</p>}

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      <table className="flha-history-table">
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Instructor</th>
            <th>Training Location</th>
            <th>PDF</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center' }}>No FLHA submissions available</td>
            </tr>
          ) : (
            sorted.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.submittedAt ? new Date(entry.submittedAt).toLocaleString() : ''}</td>
                <td>{entry.instructorDisplay || entry.instructorName || ''}</td>
                <td>{entry.trainingLocation || ''}</td>
                <td>
                  <button onClick={() => handleViewPDF(entry)}>
                    View PDF
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default FLHAHistory;
